import EXECUTIVE_SCORE_CONFIG, {
  CORRELATION_STRENGTH_BANDS,
  EXECUTIVE_ANALYTICS_GRAIN,
} from "../config/executiveFocusNoteScoreConfig";

const TECHNICAL_TERM_REPLACEMENTS = [
  [/\bSQL backend\b/gi, "AI Agent"],
  [/\bSQL query engine\b/gi, "Analytics Agent"],
  [/\bBackend query\b/gi, "Executive Insight Agent"],
  [/\bbackend query\b/gi, "Executive Insight Agent"],
  [/\bSQL query\b/gi, "insight request"],
  [/\bSQL files?\b/gi, "evidence sources"],
  [/\bsource_sql_files\b/gi, "evidence_sources"],
  [/\bbackend\b/gi, "AI Agent"],
];

const ACHIEVEMENT_KEYS = [
  "achievement_pct",
  "achievement_percentage",
  "achievement_percent",
  "ach_pct",
  "ach_percent",
  "Achievement Percentage",
  "Achievement %",
  "Ach Pct",
];

const ACTUAL_SALES_KEYS = [
  "actual_sales",
  "current_sales",
  "current_month_sales",
  "CurrentMonthSales",
  "cy_sales",
  "current_year_sales",
  "net_sales",
  "sales",
];

const COMPARISON_SALES_KEYS = [
  "same_month_last_year_sales",
  "last_year_sales",
  "ly_sales",
  "previous_month_sales",
  "PreviousMonthSales",
  "comparison_sales",
  "Avg6MonthSales",
  "avg_6month_sales",
];

const BUDGET_KEYS = ["budget", "budget_sales", "target", "sales_budget"];
const GROWTH_KEYS = ["growth_pct", "growth_percent", "growth_percentage", "Growth %"];
const CONTRIBUTION_KEYS = ["contribution_pct", "contribution_percent", "Contribution %"];
const COVERAGE_KEYS = ["coverage_pct", "coverage_percent", "Coverage %"];
const COMPLIANCE_KEYS = ["compliance_pct", "compliance_percent", "Compliance %"];
const ABSOLUTE_VARIANCE_KEYS = ["absolute_variance", "absolute_growth", "sales_gap", "variance"];

const SECTION_KEYS = [
  "critical_business_alerts",
  "growth_opportunities",
  "coverage_compliance_gaps",
  "stockist_anomalies",
];

const SCORE_SECTION_MAP = {
  critical_business_alerts: "risk_criticality",
  growth_opportunities: "sales_performance",
  coverage_compliance_gaps: "coverage",
  stockist_anomalies: "stockist_performance",
};

const DATE_LABELS = {
  same_month_last_year_sales: "Same month last year",
  last_year_sales: "Same month last year",
  ly_sales: "Same month last year",
  previous_month_sales: "Previous month",
  PreviousMonthSales: "Previous month",
  Avg6MonthSales: "Six-month average",
  avg_6month_sales: "Six-month average",
};

export function sanitizeExecutiveText(value) {
  if (typeof value === "string") {
    return TECHNICAL_TERM_REPLACEMENTS.reduce(
      (text, [pattern, replacement]) => text.replace(pattern, replacement),
      value
    );
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeExecutiveText(item));
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        sanitizeExecutiveText(key),
        sanitizeExecutiveText(item),
      ])
    );
  }

  return value;
}

export function getCorrelationStrength(coefficient) {
  const value = Number(coefficient);
  if (!Number.isFinite(value)) return "Insufficient data";

  const band = CORRELATION_STRENGTH_BANDS.find(({ min, max }) => value >= min && value <= max);
  return band?.label || "Insufficient data";
}

export function safeGrowthPercent(currentSales, comparisonSales) {
  const current = toNumber(currentSales);
  const comparison = toNumber(comparisonSales);

  if (!Number.isFinite(current) || !Number.isFinite(comparison) || comparison === 0) {
    return null;
  }

  return ((current - comparison) / comparison) * 100;
}

export function formatMetricLabel(key) {
  const labels = {
    severity: "Severity",
    severity_score: "Severity Score",
    state: "State",
    hq_name: "HQ",
    brand_name: "Brand",
    employee_name: "Employee",
    stockist_name: "Stockist",
    actual_sales: "Actual Sales",
    comparison_sales: "Comparison Sales",
    budget: "Budget",
    sales_gap: "Sales Gap",
    achievement_pct: "Achievement %",
    growth_pct: "Growth %",
    mom_growth_pct: "MoM Growth %",
    contribution_pct: "Contribution %",
    coverage_pct: "Coverage %",
    compliance_pct: "Compliance %",
    absolute_variance: "Absolute Variance",
    comparison_period: "Comparison Period",
    business_reason: "Business Reason",
    score_explanation: "Score Explanation",
    growth_explanation: "Growth Details",
    stockist_status: "Stockist Status",
    citation_id: "Evidence",
  };

  return labels[key] || String(key || "").replace(/_/g, " ");
}

export function formatMetricValue(value, key = "") {
  if (value === null || value === undefined || value === "") return "-";

  if (Array.isArray(value)) return value.join("; ");

  if (typeof value === "object") return JSON.stringify(value);

  const numeric = toNumber(value);
  const lowerKey = String(key).toLowerCase();

  if (Number.isFinite(numeric)) {
    if (lowerKey.includes("pct") || lowerKey.includes("percent") || lowerKey.includes("growth")) {
      return `${roundMetric(numeric)}%`;
    }

    if (
      lowerKey.includes("sales") ||
      lowerKey.includes("budget") ||
      lowerKey.includes("gap") ||
      lowerKey.includes("variance")
    ) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(numeric);
    }
  }

  return String(value);
}

export function buildSelectedFilterContext(filters = {}, data = {}) {
  const inferredState = filters.state || findFirstValueByKeys(data, ["state", "State"]);
  const context = {
    "Fiscal Year": filters.fiscalYearLabel || filters.fiscal_year || filters.fiscalYear,
    Month: filters.yearmonthLabel || filters.yearmonth,
    Division: filters.division,
    State: inferredState,
    BDM: filters.bdmLabel || filters.bdm,
    ZM: filters.zmLabel || filters.zm,
    HQ: filters.hq,
    Brand: filters.brand,
  };

  return Object.fromEntries(
    Object.entries(context).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );
}

export function normalizeExecutiveFocusNote(rawData, filters = {}, mode = "selected_filters") {
  const sanitized = sanitizeExecutiveText(rawData || {});
  const selectedFilterContext = buildSelectedFilterContext(filters, sanitized);
  const normalized = {
    ...sanitized,
    selected_filter_context: selectedFilterContext,
    score_configuration: EXECUTIVE_SCORE_CONFIG,
    analytics_grain: EXECUTIVE_ANALYTICS_GRAIN,
    calculation_notes: buildCalculationNotes(),
  };

  normalized.title = buildTitle(sanitized.title, selectedFilterContext);
  normalized.period = sanitized.period || selectedFilterContext.Month || (mode === "current_month" ? "Current month" : "");
  normalized.executive_focus_points = normalizeInsights(
    sanitized.executive_focus_points,
    selectedFilterContext
  );
  normalized.citation_map = normalizeCitationMap(sanitized.citation_map, selectedFilterContext);

  SECTION_KEYS.forEach((key) => {
    const section = sanitized[key];
    if (!section) return;

    normalized[key] = {
      ...section,
      table: normalizeRows(section.table || [], selectedFilterContext, SCORE_SECTION_MAP[key]),
    };
  });

  normalized.lag_correlation_guidance = buildLagCorrelationGuidance();

  return normalized;
}

function normalizeInsights(insights = [], selectedFilterContext = {}) {
  return (Array.isArray(insights) ? insights : []).map((insight) => {
    const state = insight?.state || selectedFilterContext.State;
    const scoreModule = inferScoreModule(insight);
    const scoreExplanation = buildScoreExplanation(scoreModule, insight?.score || insight?.severity_score);

    return removeEmptyValues({
      ...insight,
      state,
      score_explanation: insight?.score_explanation || scoreExplanation,
    });
  });
}

function normalizeCitationMap(citationMap = {}, selectedFilterContext = {}) {
  if (!citationMap || typeof citationMap !== "object") return {};

  return Object.fromEntries(
    Object.entries(citationMap).map(([id, citation]) => {
      const sourceSupportingRow = citation?.supporting_row || citation?.supportingRow || {};
      const supportingRow = Object.keys(sourceSupportingRow).length
        ? normalizeRow(
            sourceSupportingRow,
            selectedFilterContext,
            inferScoreModule(citation)
          )
        : undefined;
      const evidenceSources = citation?.evidence_sources || citation?.source_sql_files || [];
      const filters = {
        ...(citation?.filters || {}),
        state: citation?.filters?.state || selectedFilterContext.State,
      };

      return [
        id,
        removeEmptyValues({
          ...citation,
          evidence_sources: Array.isArray(evidenceSources) ? evidenceSources : [evidenceSources],
          source_sql_files: undefined,
          filters: removeEmptyValues(filters),
          metrics: normalizeMetricMap(citation?.metrics || {}),
          supporting_row: supportingRow,
        }),
      ];
    })
  );
}

function normalizeRows(rows = [], selectedFilterContext = {}, scoreModule = "sales_performance") {
  return (Array.isArray(rows) ? rows : []).map((row) => normalizeRow(row, selectedFilterContext, scoreModule));
}

function normalizeRow(row = {}, selectedFilterContext = {}, scoreModule = "sales_performance") {
  const normalized = { ...(row || {}) };

  removeKeys(normalized, [
    ...ACHIEVEMENT_KEYS,
    ...GROWTH_KEYS,
    ...CONTRIBUTION_KEYS,
    ...COVERAGE_KEYS,
    ...COMPLIANCE_KEYS,
  ]);

  const achievementPct = firstNumber(row, ACHIEVEMENT_KEYS);
  const growthPctFromData = firstNumber(row, GROWTH_KEYS);
  const contributionPct = firstNumber(row, CONTRIBUTION_KEYS);
  const coveragePct = firstNumber(row, COVERAGE_KEYS);
  const compliancePct = firstNumber(row, COMPLIANCE_KEYS);
  const actualSales = firstNumber(row, ACTUAL_SALES_KEYS);
  const comparison = firstComparison(row);
  const budget = firstNumber(row, BUDGET_KEYS);
  const absoluteVarianceFromData = firstNumber(row, ABSOLUTE_VARIANCE_KEYS);
  const comparisonSales = comparison.value;
  const calculatedVariance = Number.isFinite(actualSales) && Number.isFinite(comparisonSales)
    ? actualSales - comparisonSales
    : null;
  const absoluteVariance = Number.isFinite(absoluteVarianceFromData)
    ? absoluteVarianceFromData
    : calculatedVariance;
  const calculatedGrowthPct = safeGrowthPercent(actualSales, comparisonSales);
  const growthPct = Number.isFinite(growthPctFromData) && Number.isFinite(comparisonSales) && comparisonSales !== 0
    ? growthPctFromData
    : calculatedGrowthPct;

  normalized.state = row.state || row.State || selectedFilterContext.State;
  normalized.actual_sales = Number.isFinite(actualSales) ? actualSales : normalized.actual_sales;
  normalized.comparison_sales = Number.isFinite(comparisonSales) ? comparisonSales : normalized.comparison_sales;
  normalized.budget = Number.isFinite(budget) ? budget : normalized.budget;
  normalized.achievement_pct = Number.isFinite(achievementPct) ? achievementPct : normalized.achievement_pct;
  normalized.growth_pct = Number.isFinite(growthPct) ? growthPct : normalized.growth_pct;
  normalized.contribution_pct = Number.isFinite(contributionPct) ? contributionPct : normalized.contribution_pct;
  normalized.coverage_pct = Number.isFinite(coveragePct) ? coveragePct : normalized.coverage_pct;
  normalized.compliance_pct = Number.isFinite(compliancePct) ? compliancePct : normalized.compliance_pct;
  normalized.absolute_variance = Number.isFinite(absoluteVariance)
    ? absoluteVariance
    : normalized.absolute_variance;
  normalized.sales_gap = Number.isFinite(budget) && Number.isFinite(actualSales)
    ? actualSales - budget
    : normalized.sales_gap;
  normalized.comparison_period = row.comparison_period || comparison.period;
  normalized.business_reason = row.business_reason || buildBusinessReason(normalized);
  normalized.growth_explanation = row.growth_explanation || buildGrowthExplanation(
    actualSales,
    comparisonSales,
    normalized.absolute_variance,
    growthPct,
    normalized.comparison_period
  );
  normalized.score_explanation = row.score_explanation || buildScoreExplanation(
    scoreModule,
    row.severity_score || row.score
  );

  if (scoreModule === "stockist_performance" || normalized.stockist_name) {
    normalized.stockist_status = row.stockist_status || classifyStockist(
      actualSales,
      comparisonSales,
      growthPct
    );
    normalized.mom_growth_pct = normalized.comparison_period === "Previous month"
      ? growthPct
      : row.mom_growth_pct;
  }

  return removeEmptyValues(normalized);
}

function normalizeMetricMap(metrics = {}) {
  if (!metrics || typeof metrics !== "object") return metrics;

  return normalizeRow(metrics, {}, "sales_performance");
}

function buildTitle(title, selectedFilterContext = {}) {
  const baseTitle = title || "Executive Focus Note";
  const contextParts = [
    selectedFilterContext.Division,
    selectedFilterContext.State,
    selectedFilterContext.HQ,
    selectedFilterContext.Brand,
  ].filter(Boolean);

  if (!contextParts.length) return baseTitle;

  const suffix = contextParts.join(" | ");
  return baseTitle.includes(suffix) ? baseTitle : `${baseTitle} - ${suffix}`;
}

function buildGrowthExplanation(currentSales, comparisonSales, absoluteVariance, growthPct, comparisonPeriod) {
  const current = toNumber(currentSales);
  const comparison = toNumber(comparisonSales);

  if (!Number.isFinite(current) || !Number.isFinite(comparison)) {
    return "Growth is not calculated because current or comparison sales are unavailable.";
  }

  if (comparison === 0) {
    return "Growth is not calculated because comparison-period sales are zero.";
  }

  const period = comparisonPeriod || "comparison period";
  const growthLabel = Number.isFinite(growthPct) ? formatMetricValue(growthPct, "growth_pct") : "-";

  return [
    `Current Sales: ${formatMetricValue(current, "actual_sales")}`,
    `${period}: ${formatMetricValue(comparison, "comparison_sales")}`,
    `Absolute Change: ${formatMetricValue(absoluteVariance, "absolute_variance")}`,
    `Growth: ${growthLabel}`,
    period === "Same month last year"
      ? "YoY Growth % = ((Current Month Sales - Same Month Last Year Sales) / Same Month Last Year Sales) x 100."
      : "MoM Growth % = ((Current Month Sales - Previous Month Sales) / Previous Month Sales) x 100.",
  ].join(" | ");
}

function buildScoreExplanation(moduleKey = "sales_performance", score) {
  const config = EXECUTIVE_SCORE_CONFIG[moduleKey] || EXECUTIVE_SCORE_CONFIG.sales_performance;
  const weights = Object.entries(config.weights || {})
    .map(([metric, weight]) => `${humanizeCamel(metric)} ${roundMetric(weight * 100)}%`)
    .join(", ");
  const scoreText = Number.isFinite(toNumber(score)) ? ` Current score: ${roundMetric(score)}.` : "";

  return `${config.label} score uses ${weights}. Final score range is ${config.scoreRange}.${scoreText} ${config.missingData}`;
}

function buildBusinessReason(row = {}) {
  const reasons = [];

  if (Array.isArray(row.severity_reasons) && row.severity_reasons.length) {
    reasons.push(row.severity_reasons[0]);
  }

  if (Number.isFinite(toNumber(row.sales_gap)) && toNumber(row.sales_gap) < 0) {
    reasons.push("Actual sales are below budget.");
  }

  if (Number.isFinite(toNumber(row.growth_pct)) && toNumber(row.growth_pct) < 0) {
    reasons.push("Sales declined versus the comparison period.");
  }

  if (Number.isFinite(toNumber(row.achievement_pct)) && toNumber(row.achievement_pct) < 80) {
    reasons.push("Achievement % is below the warning threshold.");
  }

  return reasons[0] || row.severity || "Flagged by Executive Insight Agent evidence rules.";
}

function buildCalculationNotes() {
  return {
    achievement_label: "Achievement %",
    yoy_growth_formula:
      "YoY Growth % = ((Current Month Sales - Same Month Last Year Sales) / Same Month Last Year Sales) x 100",
    mom_growth_formula:
      "MoM Growth % = ((Current Month Sales - Previous Month Sales) / Previous Month Sales) x 100",
    zero_comparison_rule:
      "Growth percentages are suppressed when the comparison value is zero or missing.",
    duplicate_metric_rule:
      "Achievement Percentage, Achievement %, and Ach Pct are consolidated as Achievement %.",
  };
}

function buildLagCorrelationGuidance() {
  return {
    grain: EXECUTIVE_ANALYTICS_GRAIN,
    lag_features: [
      "Coverage Lag 1",
      "Coverage Lag 2",
      "Coverage Lag 3",
      "Compliance Lag 1",
      "Compliance Lag 2",
      "Compliance Lag 3",
      "Three-month average coverage",
      "Three-month average compliance",
      "Three-month coverage trend",
      "Three-month compliance trend",
      "Consecutive months below threshold",
      "Coverage volatility",
      "Compliance volatility",
    ],
    correlation_language:
      "Correlation is described as associated with, related to, or may have contributed to. It is not described as causation.",
  };
}

function classifyStockist(currentSales, comparisonSales, growthPct) {
  const current = toNumber(currentSales);
  const comparison = toNumber(comparisonSales);
  const growth = toNumber(growthPct);

  if (Number.isFinite(current) && current > 0 && (!Number.isFinite(comparison) || comparison === 0)) {
    return "New";
  }

  if ((!Number.isFinite(current) || current === 0) && Number.isFinite(comparison) && comparison > 0) {
    return "Inactive";
  }

  if (!Number.isFinite(growth)) return "Stable";
  if (growth >= 5) return "Growing";
  if (growth <= -5) return "Declining";
  return "Stable";
}

function firstComparison(row = {}) {
  for (const key of COMPARISON_SALES_KEYS) {
    const value = toNumber(row[key]);
    if (Number.isFinite(value)) {
      return {
        value,
        period: row.comparison_period || DATE_LABELS[key] || "Comparison period",
      };
    }
  }

  return { value: null, period: row.comparison_period || "" };
}

function firstNumber(row = {}, keys = []) {
  for (const key of keys) {
    const value = toNumber(row?.[key]);
    if (Number.isFinite(value)) return value;
  }

  return null;
}

function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const cleaned = String(value).replace(/[%,$\s]/g, "");
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

function removeKeys(target, keys = []) {
  keys.forEach((key) => {
    delete target[key];
  });
}

function removeEmptyValues(source = {}) {
  if (!source || typeof source !== "object" || Array.isArray(source)) return source;

  return Object.fromEntries(
    Object.entries(source).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );
}

function findFirstValueByKeys(value, keys = []) {
  if (!value || typeof value !== "object") return "";

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findFirstValueByKeys(item, keys);
      if (found) return found;
    }
    return "";
  }

  for (const key of keys) {
    if (value[key]) return value[key];
  }

  for (const item of Object.values(value)) {
    const found = findFirstValueByKeys(item, keys);
    if (found) return found;
  }

  return "";
}

function inferScoreModule(value = {}) {
  const text = [
    value?.focus_area,
    value?.module,
    value?.section,
    value?.category,
    value?.insight,
  ].filter(Boolean).join(" ").toLowerCase();

  if (text.includes("stockist")) return "stockist_performance";
  if (text.includes("brand")) return "brand_performance";
  if (text.includes("hq")) return "hq_performance";
  if (text.includes("coverage")) return "coverage";
  if (text.includes("compliance")) return "compliance";
  if (text.includes("doctor")) return "doctor_coverage";
  if (text.includes("tm") || text.includes("employee")) return "tm_performance";
  if (text.includes("risk") || text.includes("critical")) return "risk_criticality";
  return "sales_performance";
}

function humanizeCamel(value = "") {
  return String(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (char) => char.toUpperCase());
}

function roundMetric(value) {
  const numeric = toNumber(value);
  if (!Number.isFinite(numeric)) return value;
  return Math.round(numeric * 100) / 100;
}
